/**
 * SideNav Component
 *
 * Collapsible left-hand sidebar navigation with grouped nav items.
 * Groups expand/collapse with state persisted in localStorage.
 */
import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Tooltip,
  Caption1,
  Body1,
} from '@fluentui/react-components';
import {
  Home24Regular,
  Home24Filled,
  ShieldCheckmark24Regular,
  ShieldCheckmark24Filled,
  LockClosed24Regular,
  LockClosed24Filled,
  Certificate24Regular,
  Certificate24Filled,
  Stethoscope24Regular,
  Stethoscope24Filled,
  Toolbox24Regular,
  Toolbox24Filled,
  PlugConnected24Regular,
  PlugConnected24Filled,
  People24Regular,
  People24Filled,
  Key24Regular,
  Key24Filled,
  Settings24Regular,
  Settings24Filled,
  Navigation24Regular,
  ChevronDown24Regular,
  ChevronRight24Regular,
  Play24Regular,
  Play24Filled,
  DataTrending24Regular,
  DataTrending24Filled,
  Grid24Regular,
  Grid24Filled,
  DataPie24Regular,
  DataPie24Filled,
  Checkmark24Regular,
  Checkmark24Filled,
  BranchFork24Regular,
  BranchFork24Filled,
  Cloud24Regular,
  Cloud24Filled,
  TextBulletListSquare24Regular,
  TextBulletListSquare24Filled,
  Board24Regular,
  Board24Filled,
  Box24Regular,
  Box24Filled,
  Bot24Regular,
  Bot24Filled,
  DocumentBulletList24Regular,
  DocumentBulletList24Filled,
  History24Regular,
  History24Filled,
  Alert24Regular,
  Alert24Filled,
  Receipt24Regular,
  Receipt24Filled,
  ShieldLock24Regular,
  ShieldLock24Filled,
  Shield24Regular,
  Shield24Filled,
  ShareAndroid24Regular,
  ShareAndroid24Filled,
  Gavel24Regular,
  Gavel24Filled,
  ScanDash24Regular,
  ScanDash24Filled,
  Wrench24Regular,
  Wrench24Filled,
  Lightbulb24Regular,
  Lightbulb24Filled,
  ShieldError24Regular,
  ShieldError24Filled,
  Database24Regular,
  Database24Filled,
  ShieldKeyhole24Regular,
  ShieldKeyhole24Filled,
  BrainCircuit24Regular,
  BrainCircuit24Filled,
  Sparkle24Regular,
  Sparkle24Filled,
  BotAdd24Regular,
  BotAdd24Filled,
  Server24Regular,
  Server24Filled,
  CheckmarkCircle24Regular,
  CheckmarkCircle24Filled,
  Gavel24Regular as RuntimeGavel24Regular,
  Gavel24Filled as RuntimeGavel24Filled,
  ShieldKeyhole24Regular as TrustShieldKeyhole24Regular,
  ShieldKeyhole24Filled as TrustShieldKeyhole24Filled,
  PulseSquare24Regular,
  PulseSquare24Filled,
  Beaker24Regular,
  Beaker24Filled,
  bundleIcon,
} from '@fluentui/react-icons';
import { useAppStore } from '../../stores/appStore';
import { useAuth, hasAnyRole } from '../../features/authentication';
import { useIsAuditor } from '../../hooks/useIsAuditor';
import { solutionTokens, categoryColors } from '../../theme/solution-theme';

// Bundle icons for filled/regular states
const HomeIcon = bundleIcon(Home24Filled, Home24Regular);
const EnforcementIcon = bundleIcon(ShieldCheckmark24Filled, ShieldCheckmark24Regular);
const EvidenceIcon = bundleIcon(Certificate24Filled, Certificate24Regular);
const DiagnosticIcon = bundleIcon(Stethoscope24Filled, Stethoscope24Regular);
const SecurityIcon = bundleIcon(LockClosed24Filled, LockClosed24Regular);
const DevToolsIcon = bundleIcon(Toolbox24Filled, Toolbox24Regular);
const IntegrationsIcon = bundleIcon(PlugConnected24Filled, PlugConnected24Regular);
const UsersIcon = bundleIcon(People24Filled, People24Regular);
const ApiKeysIcon = bundleIcon(Key24Filled, Key24Regular);
const SettingsIcon = bundleIcon(Settings24Filled, Settings24Regular);
const RunsIcon = bundleIcon(Play24Filled, Play24Regular);
const RiskIcon = bundleIcon(DataTrending24Filled, DataTrending24Regular);
const FrameworksIcon = bundleIcon(Grid24Filled, Grid24Regular);
const ScorecardIcon = bundleIcon(DataPie24Filled, DataPie24Regular);
const AttestationsIcon = bundleIcon(Checkmark24Filled, Checkmark24Regular);
const PRBotIcon = bundleIcon(BranchFork24Filled, BranchFork24Regular);
const EnvironmentsIcon = bundleIcon(Cloud24Filled, Cloud24Regular);
const ControlRoomIcon = bundleIcon(TextBulletListSquare24Filled, TextBulletListSquare24Regular);
const BoardIcon = bundleIcon(Board24Filled, Board24Regular);
const DependenciesIcon = bundleIcon(Box24Filled, Box24Regular);
const AIAdvisorIcon = bundleIcon(Bot24Filled, Bot24Regular);
const ReportsIcon = bundleIcon(DocumentBulletList24Filled, DocumentBulletList24Regular);
const AuditLogIcon = bundleIcon(History24Filled, History24Regular);
const NotificationsIcon = bundleIcon(Alert24Filled, Alert24Regular);
const BillingIcon = bundleIcon(Receipt24Filled, Receipt24Regular);
const MergeGateIcon = bundleIcon(ShieldLock24Filled, ShieldLock24Regular);
const GuardrailsIcon = bundleIcon(Shield24Filled, Shield24Regular);
const KnowledgeGraphIcon = bundleIcon(ShareAndroid24Filled, ShareAndroid24Regular);
const ConstitutionIcon = bundleIcon(Gavel24Filled, Gavel24Regular);
const ScannersIcon = bundleIcon(ScanDash24Filled, ScanDash24Regular);
const AutorepairIcon = bundleIcon(Wrench24Filled, Wrench24Regular);
const PolicyIntelIcon = bundleIcon(Lightbulb24Filled, Lightbulb24Regular);
const AttackPathsIcon = bundleIcon(ShieldError24Filled, ShieldError24Regular);
const ShieldIcon = bundleIcon(ShieldKeyhole24Filled, ShieldKeyhole24Regular);
const DataExposureIcon = bundleIcon(Database24Filled, Database24Regular);
const AIGovernanceIcon = bundleIcon(BrainCircuit24Filled, BrainCircuit24Regular);
const LightbulbIcon = bundleIcon(Sparkle24Filled, Sparkle24Regular);
const AgentRegistryIcon = bundleIcon(BotAdd24Filled, BotAdd24Regular);
const McpGovernanceIcon = bundleIcon(Server24Filled, Server24Regular);
const GovernanceApprovalsIcon = bundleIcon(CheckmarkCircle24Filled, CheckmarkCircle24Regular);
const RuntimeDecisionsIcon = bundleIcon(RuntimeGavel24Filled, RuntimeGavel24Regular);
const IdentityTrustIcon = bundleIcon(TrustShieldKeyhole24Filled, TrustShieldKeyhole24Regular);
const GovernanceDriftIcon = bundleIcon(PulseSquare24Filled, PulseSquare24Regular);
const ExecutionSimulatorIcon = bundleIcon(Beaker24Filled, Beaker24Regular);

const useStyles = makeStyles({
  nav: {
    display: 'flex',
    flexDirection: 'column',
    width: '240px',
    height: 'calc(100vh - 48px)',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    position: 'fixed',
    // 48px matches AppHeader's fixed header height (32px + 16px).
    top: `calc(${tokens.spacingVerticalXXXL} + ${tokens.spacingVerticalL})`,
    left: 0,
    zIndex: 900,
    transition: 'width 0.2s ease, transform 0.2s ease',
    overflow: 'hidden',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  navCollapsed: {
    width: '48px',
  },
  navHidden: {
    transform: 'translateX(-100%)',
  },
  navHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: '48px',
  },
  hamburgerButton: {
    minWidth: '32px',
    width: '32px',
    height: '32px',
  },
  navContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: tokens.spacingVerticalS,
    overflowY: 'auto',
    overflowX: 'hidden',
    gap: tokens.spacingVerticalXS,
  },
  navGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  groupLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    userSelect: 'none',
  },
  groupChevron: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    transition: 'transform 0.15s ease',
  },
  groupItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    overflow: 'hidden',
  },
  groupItemsCollapsed: {
    display: 'none',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    paddingLeft: tokens.spacingHorizontalXL,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    transition: 'background-color 0.1s ease',
    textDecoration: 'none',
    color: tokens.colorNeutralForeground1,
    border: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    justifyContent: 'flex-start',
    minHeight: '36px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemTopLevel: {
    paddingLeft: tokens.spacingHorizontalM,
    minHeight: '40px',
  },
  navItemActive: {
    backgroundColor: solutionTokens.brandPrimary,
    color: tokens.colorNeutralForegroundStaticInverted,
    ':hover': {
      backgroundColor: solutionTokens.brandPrimaryHover,
    },
  },
  navItemCollapsed: {
    justifyContent: 'center',
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingVerticalS,
  },
  navItemText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: tokens.fontSizeBase200,
  },
});

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ filled?: boolean }>;
  path: string;
  roles?: string[];
  /** Hide this item from auditor-only users */
  hideFromAuditor?: boolean;
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
  /** Hide entire group from auditor-only users */
  hideFromAuditor?: boolean;
  /** Category accent color for the group header */
  accentColor?: string;
}

const STORAGE_KEY = 'ls-nav-groups';

// F045 Object-Based IA: the seven function sections (Command/Enforcement/Assurance/Signals/
// Intelligence/Model/Automation) collapse into six object sections — Estate / Graph / Risk /
// Decisions / Evidence & Assurance / Autonomous Operations — each answering exactly one
// question a customer already asks. See src/ia/iaSurfaceRegistry.ts for the full legacy-route
// -> section -> disposition map this grouping is derived from, and F045-PROGRESS.md for the
// F5 duplicate-surface resolutions (nav items removed here; their routes now redirect).
const navGroups: NavGroup[] = [
  // ─── ESTATE — "what do I own?" ─────────────────────────────────────────────
  {
    id: 'estate',
    label: 'Estate',
    accentColor: categoryColors.environment,
    items: [
      { id: 'connected-sources', label: 'Connected Sources', icon: IntegrationsIcon, path: '/collectors' },
      { id: 'integrations', label: 'Integrations', icon: IntegrationsIcon, path: '/integrations' },
      { id: 'environments', label: 'Environments', icon: EnvironmentsIcon, path: '/environments' },
      { id: 'developer-tools', label: 'Developer Tools', icon: DevToolsIcon, path: '/developer-tools', hideFromAuditor: true },
      { id: 'agent-registry', label: 'Agent Registry', icon: AgentRegistryIcon, path: '/agents' },
      { id: 'mcp-governance', label: 'MCP Servers', icon: McpGovernanceIcon, path: '/mcp-governance' },
      { id: 'diagnostic', label: 'Diagnostic', icon: DiagnosticIcon, path: '/diagnostic' },
    ],
  },
  // ─── GRAPH — "how is everything connected?" (the foundation every other section is a lens on) ──
  {
    id: 'graph',
    label: 'Graph',
    accentColor: categoryColors.ai,
    items: [
      { id: 'governance-graph', label: 'Governance Graph', icon: KnowledgeGraphIcon, path: '/graph' },
    ],
  },
  // ─── RISK — "what can hurt me?" ─────────────────────────────────────────────
  {
    id: 'risk',
    label: 'Risk',
    accentColor: categoryColors.risk,
    items: [
      { id: 'attack-paths', label: 'Attack Paths', icon: AttackPathsIcon, path: '/attack-paths' },
      { id: 'data-exposure', label: 'Data Exposure', icon: DataExposureIcon, path: '/data-exposure' },
      { id: 'executive-risk', label: 'Executive Risk', icon: RiskIcon, path: '/executive-risk', hideFromAuditor: true },
      { id: 'risk-forecast', label: 'Risk Forecast', icon: RiskIcon, path: '/predictive' },
      { id: 'security-signals', label: 'Security Signals', icon: ScannersIcon, path: '/scanners' },
      { id: 'findings', label: 'Findings', icon: SecurityIcon, path: '/security' },
      { id: 'dependencies', label: 'Dependencies', icon: DependenciesIcon, path: '/dependencies' },
      { id: 'governance-drift', label: 'Governance Drift', icon: GovernanceDriftIcon, path: '/governance-drift' },
      { id: 'trust', label: 'Identity Trust', icon: IdentityTrustIcon, path: '/trust' },
    ],
  },
  // ─── DECISIONS — "what did governance decide?" ─────────────────────────────
  {
    id: 'decisions',
    label: 'Decisions',
    accentColor: categoryColors.compliance,
    items: [
      { id: 'control-plane', label: 'Control Plane', icon: HomeIcon, path: '/dashboard' },
      { id: 'governance-loop', label: 'Governance Loop', icon: HomeIcon, path: '/governance-loop' },
      { id: 'board-view', label: 'Board View', icon: BoardIcon, path: '/board', hideFromAuditor: true },
      { id: 'runtime-enforcement', label: 'Runtime Enforcement', icon: ControlRoomIcon, path: '/control-room', hideFromAuditor: true },
      { id: 'policies', label: 'Policies', icon: GuardrailsIcon, path: '/guardrails', hideFromAuditor: true },
      { id: 'policy-runs', label: 'Decision Log', icon: RunsIcon, path: '/runs', hideFromAuditor: true },
      { id: 'merge-governance', label: 'Merge Governance', icon: PRBotIcon, path: '/pr-bot', hideFromAuditor: true },
      { id: 'constitution', label: 'Core Enforcement Rules', icon: ConstitutionIcon, path: '/constitution' },
      { id: 'approvals', label: 'Approvals', icon: AttestationsIcon, path: '/attestations', hideFromAuditor: true },
      { id: 'exceptions', label: 'Risk Acceptance', icon: ShieldIcon, path: '/exceptions', hideFromAuditor: true },
      { id: 'runtime-decisions', label: 'Runtime Decisions', icon: RuntimeDecisionsIcon, path: '/runtime-governance' },
    ],
  },
  // ─── EVIDENCE & ASSURANCE — "can I prove it?" ──────────────────────────────
  {
    id: 'evidence-assurance',
    label: 'Evidence & Assurance',
    accentColor: categoryColors.code,
    items: [
      { id: 'controls', label: 'Controls', icon: ScorecardIcon, path: '/scorecard' },
      { id: 'frameworks', label: 'Frameworks', icon: FrameworksIcon, path: '/frameworks' },
      { id: 'essential-eight', label: 'Essential Eight', icon: ShieldIcon, path: '/essential-eight' },
      { id: 'evidence-chain', label: 'Proof Chain', icon: EvidenceIcon, path: '/evidence' },
      { id: 'reports', label: 'Reports', icon: ReportsIcon, path: '/reports' },
      { id: 'continuous-assurance', label: 'Continuous Assurance', icon: EnforcementIcon, path: '/assurance' },
      { id: 'governance-posture', label: 'Governance Posture', icon: EnforcementIcon, path: '/governance-posture' },
      { id: 'audit-workspace', label: 'Audit Workspace', icon: AttestationsIcon, path: '/audit' },
    ],
  },
  // ─── AUTONOMOUS OPERATIONS — "what is operating on my behalf?" ─────────────
  {
    id: 'autonomous-operations',
    label: 'Autonomous Operations',
    accentColor: categoryColors.ai,
    hideFromAuditor: true,
    items: [
      { id: 'ai-governance', label: 'AI Governance', icon: AIGovernanceIcon, path: '/ai-governance' },
      { id: 'code-governance', label: 'AI Code Governance', icon: AIGovernanceIcon, path: '/code-governance', hideFromAuditor: true },
      { id: 'autonomous-governance', label: 'Autonomous Governance', icon: GovernanceApprovalsIcon, path: '/autonomous-governance', hideFromAuditor: true },
      { id: 'ai-advisor', label: 'AI Advisor', icon: AIAdvisorIcon, path: '/ai-advisor', hideFromAuditor: true },
      { id: 'execution-simulator', label: 'Simulation', icon: ExecutionSimulatorIcon, path: '/execution-simulator' },
      { id: 'autorepair', label: 'Autorepair', icon: AutorepairIcon, path: '/autorepair', hideFromAuditor: true },
      { id: 'policy-intelligence', label: 'Policy Intelligence', icon: PolicyIntelIcon, path: '/policy-intelligence' },
      { id: 'policy-recommendations', label: 'Recommendations', icon: LightbulbIcon, path: '/policy-intelligence/recommendations' },
      { id: 'benchmark-insights', label: 'Benchmark Insights', icon: RiskIcon, path: '/policy-intelligence/benchmarks' },
    ],
  },
  // ─── SETTINGS — account/admin chrome, orthogonal to the six object sections ─
  {
    id: 'settings',
    label: 'Settings',
    hideFromAuditor: true,
    items: [
      { id: 'users', label: 'Users & Roles', icon: UsersIcon, path: '/settings/users', roles: ['System.Admin', 'Organization.Admin'] },
      { id: 'api-keys', label: 'API Keys', icon: ApiKeysIcon, path: '/settings/api-keys', roles: ['System.Admin', 'Organization.Admin'] },
      { id: 'notifications', label: 'Notifications', icon: NotificationsIcon, path: '/settings/notifications', roles: ['System.Admin', 'Organization.Admin'] },
      { id: 'billing', label: 'Billing & Usage', icon: BillingIcon, path: '/settings/billing', roles: ['System.Admin', 'Organization.Admin'] },
      { id: 'merge-gate', label: 'Merge Gate', icon: MergeGateIcon, path: '/settings/merge-gate', roles: ['System.Admin', 'Organization.Admin'] },
      { id: 'audit-log', label: 'Audit Log', icon: AuditLogIcon, path: '/audit-log', roles: ['System.Admin', 'Organization.Admin', 'Auditor'] },
      { id: 'general', label: 'General', icon: SettingsIcon, path: '/settings/general', roles: ['System.Admin', 'Organization.Admin'] },
      { id: 'sso', label: 'SSO', icon: SettingsIcon, path: '/settings/sso', roles: ['System.Admin', 'Organization.Admin'] },
    ],
  },
];

function loadExpandedGroups(): Record<string, boolean> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  // Default: Decisions group expanded (hosts the Control Plane / Executive Command landing)
  return { 'decisions': true };
}

export function SideNav() {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { isSideNavCollapsed, isMobile, toggleSideNav } = useAppStore();
  const { user } = useAuth();
  const { isAuditorOnly } = useIsAuditor();

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(loadExpandedGroups);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Auto-expand group containing the active route
  useEffect(() => {
    for (const group of navGroups) {
      if (group.items.some((item) => isActive(item.path))) {
        setExpandedGroups((prev) => {
          if (prev[group.id]) return prev;
          return { ...prev, [group.id]: true };
        });
        break;
      }
    }
  }, [location.pathname]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  }, []);

  const userHasAnyRole = (roles: string[]): boolean => hasAnyRole(user?.roles, roles);

  const isActive = (path: string): boolean => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  const renderNavItem = (item: NavItem, isTopLevel = false) => {
    if (item.roles && !userHasAnyRole(item.roles)) return null;
    if (isAuditorOnly && item.hideFromAuditor) return null;

    const active = isActive(item.path);
    const Icon = item.icon;

    const buttonClassName = mergeClasses(
      styles.navItem,
      isTopLevel && styles.navItemTopLevel,
      active && styles.navItemActive,
      isSideNavCollapsed && styles.navItemCollapsed
    );

    const button = (
      <Button
        key={item.id}
        appearance="subtle"
        className={buttonClassName}
        onClick={() => handleNavClick(item.path)}
        icon={<Icon filled={active} />}
      >
        {!isSideNavCollapsed && <Body1 className={styles.navItemText}>{item.label}</Body1>}
      </Button>
    );

    if (isSideNavCollapsed) {
      return (
        <Tooltip key={item.id} content={item.label} relationship="label" positioning="after">
          {button}
        </Tooltip>
      );
    }

    return button;
  };

  const renderGroup = (group: NavGroup) => {
    if (isAuditorOnly && group.hideFromAuditor) return null;
    const visibleItems = group.items.filter((item) => {
      if (item.roles && !userHasAnyRole(item.roles)) return false;
      if (isAuditorOnly && item.hideFromAuditor) return false;
      return true;
    });
    if (visibleItems.length === 0) return null;

    const isExpanded = expandedGroups[group.id] ?? false;
    const hasActiveItem = visibleItems.some((item) => isActive(item.path));

    if (isSideNavCollapsed) {
      // When collapsed, show just the icons without group headers
      return (
        <div key={group.id} className={styles.navGroup}>
          {visibleItems.map((item) => renderNavItem(item))}
        </div>
      );
    }

    return (
      <div key={group.id} className={styles.navGroup}>
        <div
          className={styles.groupHeader}
          onClick={() => toggleGroup(group.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleGroup(group.id); }}
          aria-expanded={isExpanded}
        >
          <Caption1
            className={styles.groupLabel}
            style={
              hasActiveItem && !isExpanded
                ? { color: solutionTokens.brandPrimaryText }
                : group.accentColor
                  ? { color: group.accentColor }
                  : undefined
            }
          >
            {group.label}
          </Caption1>
          <span className={styles.groupChevron}>
            {isExpanded ? <ChevronDown24Regular /> : <ChevronRight24Regular />}
          </span>
        </div>
        <div className={mergeClasses(styles.groupItems, !isExpanded && styles.groupItemsCollapsed)}>
          {visibleItems.map((item) => renderNavItem(item))}
        </div>
      </div>
    );
  };

  const navClassName = mergeClasses(
    styles.nav,
    isSideNavCollapsed && styles.navCollapsed,
    isMobile && isSideNavCollapsed && styles.navHidden
  );

  return (
    <nav className={navClassName} aria-label="Main navigation">
      <div className={styles.navHeader}>
        <Tooltip content={isSideNavCollapsed ? 'Expand menu' : 'Collapse menu'} relationship="label" positioning="after">
          <Button
            appearance="subtle"
            icon={<Navigation24Regular />}
            className={styles.hamburgerButton}
            onClick={toggleSideNav}
            aria-label={isSideNavCollapsed ? 'Expand menu' : 'Collapse menu'}
          />
        </Tooltip>
      </div>

      <div className={styles.navContent}>
        {navGroups.map(renderGroup)}
      </div>
    </nav>
  );
}
